import React from 'react'
import { domToReact } from 'html-react-parser'
import HPSupportSpecificLink from '../components/HPSupportSpecificLink'
import FooterNote from '../components/SidebarSubOptions/FooterNote'

const builtInReplacerMap = {
  HP_SUPPORT_PRODUCT_LINK: HPSupportSpecificLink,
  'footer-note': FooterNote
}

/**
 * Resolves a single data-replacer node into a React element.
 * Supports both built-in replacers owned by this file and caller-provided replacements.
 *
 * @param {Object} params
 * @param {Object} params.domNode - The parsed DOM node being inspected.
 * @param {Object} params.parserOptions - html-react-parser options used to render nested children.
 * @param {Object.<string, React.ReactNode>} [params.replacements={}] - Caller-provided replacer mappings.
 * @returns {React.ReactNode|undefined} A replacement node, or undefined when the DOM node should be left unchanged.
 */
export const resolveReplacerTag = ({
  domNode,
  parserOptions,
  replacements = {}
}) => {
  if (!(domNode.type === 'tag' && domNode?.attribs?.['data-replacer'])) {
    return undefined
  }

  const replacerId = domNode.attribs['data-replacer']
  const BuiltInReplacer = builtInReplacerMap[replacerId]

  if (BuiltInReplacer) {
    return (
      <BuiltInReplacer>
        {domToReact(domNode.children || [], parserOptions)}
      </BuiltInReplacer>
    )
  }

  if (Object.prototype.hasOwnProperty.call(replacements, replacerId)) {
    const replacement = replacements[replacerId]

    if (
      !React.isValidElement(replacement) ||
      typeof replacement.type === 'string' ||
      replacement.type === React.Fragment
    ) {
      return replacement
    }

    const replacementProps = replacement?.props ?? {}
    const replacementChildren =
      replacementProps.children === undefined
        ? domToReact(domNode.children || [], parserOptions)
        : replacementProps.children

    return React.cloneElement(
      replacement,
      {
        attribs: domNode.attribs || {},
        className:
          domNode.attribs?.['class'] || domNode.attribs?.['className'] || '',
        tagName: domNode.name
      },
      replacementChildren
    )
  }

  return undefined
}
